import React, {useEffect, useMemo, useState} from 'react';

import {Button, HStack, Input, NumberInput, Spinner, VStack} from '@chakra-ui/react';

import {useForm} from '@/uses/useForm';
import {useApi} from '@/uses/useApi';
import {Field, FileInput, LanguageField, NestedSelect} from "@/components";
import {useLanguage} from "@/contexts/languageContext";
import objectUtil from "@/utils/objectUtil";

export const CreateOrEditPOICategory = (props) => {
    const categoryId = props?.id;

    const [state, setState] = useState(-1);
    const {getLangObject, language, t} = useLanguage();
    const [poiCategory, setPOICategory] = useState(null);
    const [categories, setCategories] = useState(null);
    const api = useApi();
    const {formData, formFieldErrors, handleInputChange, handleInputChanges, handleFormSubmit} = useForm(
        {
            label: getLangObject(),
            categoryId: null,
            entityType: 'point_of_interest',
            thumbnail: null,
            sortOrder: 0
        },
        {
            label: {
                custom: {
                    function: (value) => {
                        return Object.values(value).some((value) => value && value.length >= 3)
                    },
                    message: t('errors.pleaseFillOrKeepEmpty')
                },
            },
        }
    );

    useEffect(() => {
        setState(() => 0);
        api.ApiService.get('category', {
            getAll: 'bool:true'
        })
            .then((res) => {
                if (res?.data?.type === 'success' && res?.data?.value) {
                    let tmp = objectUtil.plainToTree(res.data.value ?? [], 'id', 'parent');
                    tmp = objectUtil.treeToPlain(tmp, 'children', 'id', 'parent');
                    setCategories(() => tmp);
                }
            })
        if (categoryId) {
            api.ApiService.getOne('category', categoryId, {
                attributes: ['id', 'label', 'thumbnail', 'categoryId', 'sortOrder'],
            })
                .then((res) => {
                    if (res?.data?.type === 'success' && res?.data?.value) {
                        setPOICategory(() => res.data.value);
                    }
                })
        }
    }, []);

    useEffect(() => {
        if (poiCategory !== null) {
            handleInputChanges(poiCategory);
        }
        /** If categories are retrieved, and we're in create mode
         * or poi and categories are retrieved, and we're in edit mode
         */
        if ((!categoryId || poiCategory !== null) && categories !== null) {
            setState(() => 1)
        }
    }, [categoryId, poiCategory, categories]);

    const categoriesTree = useMemo(() => {
        return (categories ?? []).map((e) => ({...e, value: e.id, label: e?.[`${language}Label`] ?? ''}))
    }, [categories, language]);

    return (
        <VStack h="100%" overflow="hidden" p="1.5rem 0" gap="5vh" w="100%">
            {
                state === 1 ?
                    <VStack overflow="hidden" flex={1} gap="5" p="5" w="100%" align="center" justify="flex-start">
                        <VStack flex={1} px={2} w="100%" h="80vh" position="relative" overflow="auto">
                            <LanguageField
                                id="label"
                                errors={formFieldErrors.label}
                                label={t('wrappers.mandatoryLabel', { value: t('fields.label') })}
                                renderElement={(lang) => <Input
                                    value={formData.label[lang]}
                                    onChange={(e) => {
                                        handleInputChange('label', {...formData.label, [lang]: e?.target?.value ?? ''})
                                    }}
                                    placeholder={t('pois.poiTitle')}
                                />}
                            />
                            <HStack w="100%" gap="2rem">
                                <FileInput
                                    hasPreview
                                    onChange={(file) => handleInputChange('thumbnail', file)}
                                    file={formData.thumbnail}
                                    minH="20vw"
                                    maxH="20vw"
                                    minW="20vw"
                                    maxW="20vw"
                                />
                                <VStack>
                                    <Field
                                        label={t('wrappers.nonMandatoryLabel', { value: t('objects.category_one') })}
                                        errors={formFieldErrors.categoryId}
                                        display="flex"
                                        alignItems="center"
                                        mb="2vh"
                                    >
                                        <NestedSelect
                                            defaultValue={formData?.categoryId ? [formData?.categoryId] : []}
                                            plainTree={categoriesTree}
                                            placeholder={t('misc.selectACategory')}
                                            onValueChange={(e) => handleInputChange('categoryId', e?.value?.[0] ?? null)}
                                        />
                                    </Field>
                                    <Field
                                        label={t('wrappers.nonMandatoryLabel', { value: t('fields.order') })}
                                        errors={formFieldErrors.sortOrder}
                                        display="flex"
                                        alignItems="center"
                                        mb="2vh"
                                    >
                                        <NumberInput.Root
                                            value={formData.sortOrder}
                                            precision={0}
                                            step={1}
                                            onValueChange={(e) => handleInputChange('sortOrder', e.valueAsNumber)}
                                        >
                                            <NumberInput.Input/>
                                            <NumberInput.Control>
                                                <NumberInput.IncrementTrigger/>
                                                <NumberInput.DecrementTrigger/>
                                            </NumberInput.Control>
                                        </NumberInput.Root>
                                    </Field>
                                </VStack>
                            </HStack>
                        </VStack>
                    </VStack>
                    :
                    <VStack flex={1} w="100%" align="center" justify="flex-start">
                        <Spinner size="lg"/>
                    </VStack>
            }
            <Button
                variant="primary"
                mt="auto"
                height="auto"
                borderRadius=".5rem"
                style={{padding: '1rem 1.5rem'}}
                onClick={() => handleFormSubmit(async (data) => {
                    if (poiCategory) {
                        return api.ApiService.update('category', poiCategory.id, data);
                    }
                    return api.ApiService.create('category', data);
                }, props?.successCallback)
                }
            >
                {t('actions.validate')}
            </Button>
        </VStack>
    );
};

export default CreateOrEditPOICategory;
